import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React from 'react';
import { colorConst, colorTokenFromNestedArguments, DesignContextForwardProvider, optionalColorTokenFromNestedArguments, sizeConst, sizeTokenFromNestedArguments, useDesignContext } from '@klarna/designtokens';
import { default as FantasyColor } from '@bubble-dev/fantasy-color';
import defaultColorVariables from '@bubble-ui/particle-default-color-variables';
import { useDesignVersion } from '@bubble-ui/provider-design-version';
import { isLollipop } from '@bubble-ui/particle-design-versions';
import { mapObjIndexed, omit } from 'ramda';
import { transformColor } from '@bubble-ui/particle-colors';
import StyleSheetOverridesProvider from '@bubble-ui/provider-style-sheet-overrides';
import useDeprecationWarning from '@bubble-ui/hook-use-deprecation-warning';
// @ts-expect-error
import { ActionIconCheckmark } from './overrides/ActionIconCheckmark';
// @ts-expect-error
import { BoxSelectorOptionBase } from './overrides/BoxSelectorOptionBase';
import { Button } from './overrides/Button';
// @ts-expect-error
import { FormElement } from './overrides/FormElement';
// @ts-expect-error
import { Link } from './overrides/Link';
// @ts-expect-error
import { ListItem } from './overrides/ListItem';
// @ts-expect-error
import { RadioBullet } from './overrides/RadioBullet';
// @ts-expect-error
import { SelectorInput } from './overrides/SelectorInput';
// @ts-expect-error
import { SpacerVertical } from './overrides/SpacerVertical';
// @ts-expect-error
import { SwitchCheckbox } from './overrides/SwitchCheckbox';
// @ts-expect-error
import { TitleSecondary } from './overrides/TitleSecondary';
import { SetThemeInContext } from './SetThemeInContext';
import { ThemeContext } from './context';
var {
  hovered,
  pressed
} = transformColor;

/**
 * @deprecated this context is outdated and will be removed with v6.0.0
 */
export { ThemeContext } from './context';

/**
 * @deprecated this type is outdated and will be removed with v6.0.0
 */

/**
 * @deprecated this type is outdated and will be removed with v6.0.0
 */

var ThemeProvider = _ref => {
  var {
    theme,
    children,
    customOverrides
  } = _ref;
  useDeprecationWarning({
    componentName: 'ThemeProvider',
    removalDate: 'v6.0.0',
    useInstead: 'component tokens',
    readMore: 'https://bubble-ui.klarna.net/#components/theme-provider'
  });
  var defaultDesignContext = useDesignContext(defaultColorVariables);
  var designVersion = useDesignVersion();
  var borderRadiusParsed = typeof (theme === null || theme === void 0 ? void 0 : theme.radius_border) === 'string' ? parseInt(theme.radius_border, 10) : undefined;
  var borderRadius = borderRadiusParsed === undefined || isNaN(borderRadiusParsed) ? undefined : borderRadiusParsed < 15 ? borderRadiusParsed : 15;
  var buttonColor = theme === null || theme === void 0 ? void 0 : theme.color_button;
  var buttonColorFantasyColor = buttonColor && new FantasyColor(buttonColor);
  var buttonColorHovered = buttonColorFantasyColor ? hovered(buttonColorFantasyColor).toString() : undefined;
  var buttonColorPressed = buttonColorFantasyColor ? pressed(buttonColorFantasyColor).toString() : undefined;
  var buttonTextColor = theme === null || theme === void 0 ? void 0 : theme.color_button_text;
  var buttonTextColorFantasyColor = buttonTextColor && new FantasyColor(buttonTextColor);
  var buttonTextColorHovered = buttonTextColorFantasyColor != null ? hovered(buttonTextColorFantasyColor).toString() : undefined;
  var buttonTextColorPressed = buttonTextColorFantasyColor != null ? pressed(buttonTextColorFantasyColor).toString() : undefined;
  var checkboxColor = theme === null || theme === void 0 ? void 0 : theme.color_checkbox;
  var checkboxCheckmarkColor = theme === null || theme === void 0 ? void 0 : theme.color_checkbox_checkmark;
  var linkColor = theme === null || theme === void 0 ? void 0 : theme.color_link;
  var textColor = theme === null || theme === void 0 ? void 0 : theme.color_text;
  var designTokens = _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, !borderRadius ? {} : _objectSpread(_objectSpread({
    'box-selector/corner-radius': sizeConst(borderRadius)
  }, isLollipop(designVersion) ? {} : {
    'base-button/corner-radius': sizeConst(borderRadius),
    'base-button/keyboard-focus-corner-radius': sizeConst(borderRadius + 3)
  }), {}, {
    'dropdown/corner-radius': sizeConst(borderRadius),
    'field/corner-radius': sizeConst(borderRadius),
    'preview/corner-radius': sizeTokenFromNestedArguments({
      // This is not 1:1 the same, because before just no override happened.
      // In case corner radius for sandwich variant changes, this will be an issue, but I'm pretty sure this won't change.
      sandwich: 0,
      default: borderRadius
    }, ['borderRadiusType']),
    'tooltip/corner-radius': sizeConst(borderRadius)
  })), !buttonColor || !buttonColorFantasyColor ? {} : {
    'base-button/background-color': colorTokenFromNestedArguments({
      primary: {
        disabled: "rgba(".concat(buttonColorFantasyColor.red, ", ").concat(buttonColorFantasyColor.green, ", ").concat(buttonColorFantasyColor.blue, ", 0.5)"),
        hovered: buttonColorHovered,
        pressed: buttonColorPressed,
        default: buttonColor
      },
      secondary: {
        hovered: buttonColorHovered,
        pressed: buttonColorPressed,
        default: (_, args) => defaultDesignContext.get('colors/backgrounds/default', args)
      },
      tertiary: {
        default: (_, args) => defaultDesignContext.get('colors/backgrounds/default', args)
      },
      default: {
        default: (_, args) => defaultDesignContext.get('colors/backgrounds/subtle-floating', args)
      }
    }, ['variant', 'interactiveState']),
    'base-button/border-color': optionalColorTokenFromNestedArguments({
      primary: {
        default: buttonColor
      },
      secondary: {
        hovered: buttonColorHovered,
        pressed: buttonColorPressed,
        default: buttonColor
      },
      default: {
        default: null
      }
    }, ['variant', 'interactiveState'])
  }), !buttonTextColor ? {} : {
    'base-button/text-color': colorTokenFromNestedArguments({
      primary: {
        default: buttonTextColor
      },
      secondary: {
        hovered: buttonTextColorHovered,
        pressed: buttonTextColorPressed,
        default: buttonColor || defaultDesignContext.get('colors/text/default').toString()
      },
      default: {
        hovered: buttonColorHovered,
        pressed: buttonColorPressed,
        default: buttonColor || defaultDesignContext.get('colors/text/default').toString()
      }
    }, ['variant', 'interactiveState'])
  }), !checkboxColor ? {} : {
    'toggle/background-color': colorConst(checkboxColor)
  }), !checkboxCheckmarkColor ? {} : {
    'toggle/knob-color': colorConst(checkboxCheckmarkColor)
  }), !textColor ? {} : {
    'toggle/text-color': colorConst(textColor)
  }), !linkColor ? {} : {
    'field/link-text-color': colorConst(linkColor),
    'masked-field/link-text-color': colorConst(linkColor),
    'selector-field/link-text-color': colorConst(linkColor),
    'selector-input/link-text-color': colorConst(linkColor)
  });
  var designContext = useDesignContext(designTokens);
  if (theme == null) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, children);
  }
  var customizations = _objectSpread({}, mapObjIndexed(v => new FantasyColor(v), omit(['radius_border'], theme)));
  var borderSelectedColor = customizations.color_border_selected;
  var borderColor = customizations.color_border;
  var detailsColor = customizations.color_details;
  var headerColor = customizations.color_header;
  var spacerColor = customizations.color_spacer;
  var textSecondaryColor = customizations.color_text_secondary;
  var overrides = _objectSpread(_objectSpread({
    // Not possible to just override colors for one single icon.
    // In case reason for this is to change the checkmark color of the checkbox in SelectorOptionsMulti,
    // we need to add component design tokens for this selector to replace the overrides.
    ActionIconCheckmark: ActionIconCheckmark({
      checkboxCheckmarkColor
    }),
    // Not possible, because component doesn't have component tokens.
    // It also will not have component tokens in future, because it's a temp component.
    // To be able to replace these style sheet overrides, we need to
    // * add these props to @bubble-ui/temp-base-box-selector-option
    //   * borderColorToken
    //   * borderColorTokenArgs
    //   * borderSelectedColorToken
    //   * borderSelectedColorTokenArgs
    //   * borderRadiusToken
    //   * borderRadiusTokenArgs
    //   * textColorToken
    //   * textColorTokenArgs
    // * support component tokens for BoxSelectorDetailed
    BoxSelectorOptionBase: BoxSelectorOptionBase({
      borderColor,
      borderSelectedColor,
      textColor
    })
  }, isLollipop(designVersion) && typeof borderRadius === 'number' ? {
    ButtonPrimary: Button({
      borderRadius
    }),
    ButtonSecondary: Button({
      borderRadius
    }),
    ButtonTertiary: Button({
      borderRadius
    }),
    ButtonQuaternary: Button({
      borderRadius
    })
  } : {}), {}, {
    // Not possible, because component doesn't have component tokens for the color.
    Dropdown: FormElement({
      borderSelectedColor
    }),
    // Right now the overrides are only applied if focused && !error && !warning.
    // This is not possible when creating a theme, we'd have to manually add all the other values here.
    // But in case a color changes for Field, here it would stay the same.
    Field: FormElement({
      borderSelectedColor
    }),
    // Component is deprecated
    LinkPrimary: Link({
      linkColor
    }),
    // Component is deprecated
    LinkSecondary: Link({
      linkColor
    }),
    // Component is deprecated
    ListItem: ListItem({
      detailsColor,
      textColor
    }),
    // Skipped, because it's contrib
    PrimaryMerchantColorIcon: () => ({
      pathStyle: {
        fill: customizations.color_link
      }
    }),
    // Right now the overrides are only applied if checked && !(error || warning || disabled).
    // This is not possible when creating a theme, we'd have to manually add all the other values here.
    // But in case a color changes for RadioBullet, here it would stay the same.
    RadioBullet: RadioBullet({
      checkboxColor,
      checkboxCheckmarkColor
    }),
    // Component is deprecated
    SelectorInput: SelectorInput({
      borderColor,
      borderSelectedColor,
      textColor,
      textSecondaryColor
    }),
    // Not possible, because component doesn't have component tokens.
    SpacerVertical: SpacerVertical({
      spacerColor
    }),
    // Right now the overrides are only applied if checked && !error && !warning.
    // This is not possible when creating a theme, we'd have to manually add all the other values here.
    // But in case a color changes for SwitchCheckbox, here it would stay the same.
    SwitchCheckbox: SwitchCheckbox({
      borderColor,
      borderSelectedColor,
      checkboxColor,
      textColor
    }),
    // Component is deprecated
    TitleSecondary: TitleSecondary({
      headerColor
    })
  }, customOverrides && customOverrides({
    customizations,
    buttonColor,
    buttonColorHovered,
    buttonColorPressed,
    buttonTextColor,
    buttonTextColorHovered,
    buttonTextColorPressed,
    borderSelectedColor,
    borderColor,
    checkboxColor,
    checkboxCheckmarkColor,
    linkColor,
    detailsColor,
    headerColor,
    textColor,
    textSecondaryColor
  }));
  return /*#__PURE__*/React.createElement(StyleSheetOverridesProvider, overrides, /*#__PURE__*/React.createElement(SetThemeInContext, {
    theme: theme
  }, /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
    context: designContext
  }, children)));
};
ThemeProvider.displayName = 'ThemeProvider';

/**
 * @deprecated this provider is outdated and will be removed with v6.0.0
 */
export default ThemeProvider;

/**
 * @deprecated this HoC is outdated and will be removed with v6.0.0
 */
export var withTheme = Target => props => /*#__PURE__*/React.createElement(ThemeContext.Consumer, null, _ref2 => {
  var {
    theme
  } = _ref2;
  return /*#__PURE__*/React.createElement(Target, _extends({}, props, {
    theme: theme
  }));
});

/**
 * @deprecated this hook is outdated and will be removed with v6.0.0
 */
export var useTheme = () => React.useContext(ThemeContext);
//# sourceMappingURL=index.js.map